import { Component, OnInit, ViewEncapsulation, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';

import { MenuController, Platform, ToastController } from '@ionic/angular';

import { SplashScreen } from '@awesome-cordova-plugins/splash-screen/ngx';
import { StatusBar } from '@awesome-cordova-plugins/status-bar/ngx';

import { Storage } from '@ionic/storage';

import { AuthService } from './core/auth/auth.service';
import { UserStore } from './store/user.store';
import { RoleService } from './service/role.service';
import { AcuStorageService } from './service/acustorage.service';
import { GoogleAuthService } from './service/google-auth.service';
import { FeedbackService } from './service/feedback.service';
import { PwaUpdateService } from './core/pwa/pwa-update.service';
import { RichiesteSedeStore } from './store/richieste-sede.store';
import { DataSyncService } from './service/data-sync.service';

@Component({
    standalone: false, selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AppComponent implements OnInit {

    loggedInOauth: boolean;

    appPages = [
        {
            title: 'Home',
            url: '/app/tabs/home',
            icon: 'home'
        },
        {
            title: 'Persone',
            url: '/app/tabs/persone',
            icon: 'people'
        },
        {
            title: 'Sedi e postazioni',
            url: '/app/tabs/prenotazioni',
            icon: 'business'
        },
        {
            title: 'Le mie prenotazioni',
            url: '/app/tabs/mie-prenotazioni',
            icon: 'calendar-number'
        },
        {
            title: 'Account',
            url: '/app/tabs/account',
            icon: 'person'
        }
    ];

    loggedIn = false;
    isHr = false;
    dark: boolean;
    accessibility: boolean;
    showInstallBtn = false;
    showInstallBanner = false;
    isIosPrompt = false;
    isCollaudo = false;
    enableQaHelper = environment.enableQaHelper;
    enableFeedback = environment.enableFeedback;
    enableFeedbackOnDemand = (environment as any).enableFeedbackOnDemand ?? true;
    enableFeedbackDashboard = (environment as any).enableFeedbackDashboard ?? false;
    feedbackModalState: { isOpen: boolean; context: string } = { isOpen: false, context: 'manual' };

    constructor(
        private menu: MenuController,
        private platform: Platform,
        private router: Router,
        private splashScreen: SplashScreen,
        private statusBar: StatusBar,
        private storage: Storage,
        private toastCtrl: ToastController,
        private auth: AuthService,
        private userStore: UserStore,
        public roleService: RoleService,
        public acuStorage: AcuStorageService,
        public googleAuth: GoogleAuthService,
        private zone: NgZone,
        private feedbackService: FeedbackService,
        private pwaUpdate: PwaUpdateService,
        public richiesteSedeStore: RichiesteSedeStore,
        private dataSync: DataSyncService
    ) {
        this.initializeApp();
    }



    async ngOnInit() {
        await this.storage.create();

        this.dataSync.init();

        this.feedbackService.feedbackModal$.subscribe(state => {
            this.feedbackModalState = state;
        });

        // Check for app updates to clear old data cache
        const currentVersion = environment.acuplaceVersion;
        const storedVersion = this.acuStorage.getObjectInStorage<string>('app_version');
        if (storedVersion !== currentVersion) {
            console.log(`App version changed from ${storedVersion} to ${currentVersion}. Clearing cache...`);
            this.acuStorage.clearDataCache();
            this.acuStorage.setObjectInStorage('app_version', currentVersion);
        }

        this.isCollaudo = window.location.hostname.includes('collaudo') || 
                           window.location.hostname.includes('localhost') || 
                           window.location.hostname.includes('d6b0e') ||
                           window.location.hostname.includes('beta');

        if (!this.acuStorage.hasObjectInStorage('darkTheme')) {
            //default value -> light 
            this.acuStorage.setObjectInStorage('darkTheme', false);
        }

        this.dark = this.acuStorage.getObjectInStorage('darkTheme');
        document.body.classList.toggle('dark-theme', !!this.dark);

        if (!this.acuStorage.hasObjectInStorage('accessibility')) {
            //default value -> accessibility 
            this.acuStorage.setObjectInStorage('accessibility', false);
        }

        this.accessibility = this.acuStorage.getObjectInStorage('accessibility');

        if (this.userStore.userLogged) {
            this.updateLoggedInStatus(true);
        }

        this.listenForLoginEvents();

        this.initPwaHandling();

        this.pwaUpdate.init();

        // Cancella i cookie di Posthog sul dominio radice (.acus.eu) per evitare il blocco WAF sui WebSocket
        this.clearPosthogCookies();
    }

    private clearPosthogCookies() {
        try {
            const cookies = document.cookie.split(';');
            for (let cookie of cookies) {
                const eqPos = cookie.indexOf('=');
                const name = eqPos > -1 ? cookie.substring(0, eqPos).trim() : cookie.trim();
                if (name.startsWith('ph_') && name.includes('_posthog')) {
                    console.log(`Clearing Posthog cookie: ${name}`);
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.acus.eu;`;
                    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=acus.eu;`;
                }
            }
        } catch (e) {
            console.error('Error clearing Posthog cookies:', e);
        }
    }

    initializeApp() {
        this.platform.ready().then(() => {
            this.statusBar.styleDefault();
            this.googleAuth.initGoogleAuth()
                .catch(err => {
                    console.warn('[AppComponent] Google Auth init warning:', err);
                })
                .finally(() => {
                    this.splashScreen.hide();
                });
        });
    }

    changeTheme(event) {
        this.acuStorage.setObjectInStorage('darkTheme', event.detail.checked);
        this.dark = event.detail.checked;
        document.body.classList.toggle('dark-theme', event.detail.checked);
    }

    changeAccessibility(event) {
        this.acuStorage.setObjectInStorage('accessibility', event.detail.checked);
        this.acuStorage.accessibilitySubject.next(event.detail.checked);
    }

    updateLoggedInStatus(loggedIn: boolean) {
        console.log('update loggedIn status => ' + loggedIn);
        this.loggedIn = loggedIn;
        if (loggedIn) {
            if (this.showInstallBtn || this.isIosPrompt) {
                this.checkAndShowInstallBanner();
            }
        } else {
            this.showInstallBanner = false;
        }
    }

    listenForLoginEvents() {
        window.addEventListener('user:login', () => {
            this.updateLoggedInStatus(true);
        });

        window.addEventListener('user:signup', () => {
            this.updateLoggedInStatus(true);
        });

        window.addEventListener('user:logout', () => {
            this.updateLoggedInStatus(false);
        });
    }

    logout() {
        // gestire il logout
        console.log('logout...');
        this.userStore.logout();
        this.acuStorage.clearDataCache();
        this.acuStorage.setJwtToken('');
        this.googleAuth.logout();
        window.dispatchEvent(new CustomEvent('user:logout'));
        this.router.navigateByUrl('/login');
    }

    openFeedbackModal(context: string = 'menu_laterale') {
        this.feedbackService.openFeedbackModal(context);
    }

    onFeedbackModalDone() {
        this.feedbackService.closeFeedbackModal();
    }

    openTutorial() {
        this.menu.enable(false);
        this.storage.set('ion_did_tutorial', false);
        this.router.navigateByUrl('/tutorial');
    }

    installPwa() {
        const deferredPrompt = (window as any).deferredPrompt;
        if (deferredPrompt) {
            deferredPrompt.prompt();
            deferredPrompt.userChoice.then((choiceResult: any) => {
                if (choiceResult.outcome === 'accepted') {
                    console.log('User accepted the install prompt');
                } else {
                    console.log('User dismissed the install prompt');
                }
                (window as any).deferredPrompt = null;
                this.zone.run(() => {
                    this.showInstallBtn = false;
                    this.showInstallBanner = false;
                });
                window.dispatchEvent(new CustomEvent('pwa-installed'));
            });
        }
    }

    private initPwaHandling() {
        if (this.isAppInstalled()) {
            return;
        }

        const isIos = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
        const isSafariOrChromeIos = isIos && !this.isAppInstalled();

        this.showInstallBtn = !!(window as any).deferredPrompt;

        window.addEventListener('pwa-installable', () => {
            this.zone.run(() => {
                this.showInstallBtn = true;
                if (this.loggedIn) {
                    this.checkAndShowInstallBanner();
                }
            });
        });

        window.addEventListener('pwa-installed', () => {
            this.zone.run(() => {
                this.showInstallBtn = false;
                this.showInstallBanner = false;
            });
            localStorage.setItem('pwa_banner_dismissed_until', String(Date.now() + 365 * 24 * 60 * 60 * 1000));
        });

        if (this.showInstallBtn && this.loggedIn) {
            this.checkAndShowInstallBanner();
        } else if (isSafariOrChromeIos && this.loggedIn) {
            this.isIosPrompt = true;
            this.showInstallBtn = true;
            this.checkAndShowInstallBanner();
        }
    }

    private checkAndShowInstallBanner() {
        if (this.isAppInstalled()) {
            return;
        }
        const dismissedUntil = localStorage.getItem('pwa_banner_dismissed_until');
        if (dismissedUntil && Number(dismissedUntil) > Date.now()) {
            return;
        }

        setTimeout(() => {
            this.zone.run(() => {
                if (!this.isAppInstalled() && this.loggedIn) {
                    this.showInstallBanner = true;
                }
            });
        }, 2000);
    }

    private isAppInstalled(): boolean {
        return window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    }

    dismissInstallBanner() {
        this.showInstallBanner = false;
        // Ricorda la chiusura per 7 giorni
        localStorage.setItem('pwa_banner_dismissed_until', String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    }
}
